/**
 * Server state for build step 7 — checklists and routines. One hook per route in packages/contracts routes.step7
 * (all 21):
 *   GET /checklists (the Checklists screen: stats, This week, By client), GET /checklist-templates,
 *   POST /checklist-templates, PATCH /checklist-templates/{id}, POST /checklist-templates/{id}/items,
 *   PATCH /checklist-templates/{id}/items/{iid}, POST /checklists/apply-template,
 *   GET/POST /clients/{id}/checklists, PATCH /client-checklists/{id}, POST /client-checklists/{id}/items,
 *   PATCH /client-checklists/{id}/items/{iid}, POST/DELETE /checklist-runs/{id}/done,
 *   GET/POST /clients/{id}/routines, PATCH/DELETE /clients/{id}/routines/{rid}, POST /routines/bulk,
 *   POST/DELETE /routine-instances/{id}/done.
 *
 * Ticking a run or a routine copy also closes (or reopens) its task in the SPOC's plan, so every write refreshes
 * the work lists and the menu badges as well as the step-7 reads.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AddChecklistItemsBody,
  AddTemplateItemsBody,
  UpdateChecklistItemBody,
  UpdateClientChecklistBody,
  UpdateTemplateBody,
  UpdateTemplateItemBody,
  ApplyTemplateBody,
  BulkRoutinesBody,
  ChecklistItemState,
  CreateClientChecklistBody,
  CreateRoutineBody,
  CreateTemplateBody,
  TickProofBody,
  UpdateRoutineBody,
} from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../api';
import { refreshAfterWrite } from './work';

export const ckeys = {
  all: ['checklists'] as const,
  overview: ['checklists', 'overview'] as const,
  templates: ['checklists', 'templates'] as const,
  client: (id: string) => ['checklists', 'client', id] as const,
  routines: (id: string) => ['checklists', 'routines', id] as const,
};

export function refreshChecklists(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ckeys.all });
  refreshAfterWrite(qc);
}

// ---------------------------------------------------------------- reads

export function useChecklistsOverview() {
  return useQuery({ queryKey: ckeys.overview, queryFn: () => call('checklists.overview', { query: {} }) });
}

export function useTemplates(enabled = true) {
  return useQuery({ queryKey: ckeys.templates, queryFn: async () => (await call('checklistTemplates.list')).data, enabled });
}

export function useClientChecklists(clientId: string | null) {
  return useQuery({
    queryKey: ckeys.client(clientId ?? ''),
    queryFn: async () => (await call('checklists.listForClient', { params: { id: clientId! } })).data,
    enabled: !!clientId,
  });
}

export function useRoutines(clientId: string | null) {
  return useQuery({
    queryKey: ckeys.routines(clientId ?? ''),
    queryFn: () => call('routines.list', { params: { id: clientId! } }),
    enabled: !!clientId,
  });
}

// ---------------------------------------------------------------- writes

function useStep7Write<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => refreshChecklists(qc), onError: () => refreshChecklists(qc) });
}

export type TickKind = 'checklist_run' | 'routine_instance';

/** Tick with proof (POST …/done) or untick (DELETE …/done: "Reopened. Proof cleared."). */
export function useTick() {
  return useStep7Write((v: { kind: TickKind; id: string; proof: TickProofBody | null }): Promise<unknown> =>
    v.kind === 'checklist_run'
      ? v.proof
        ? call('checklistRuns.done', { params: { id: v.id }, body: v.proof })
        : call('checklistRuns.undo', { params: { id: v.id } })
      : v.proof
        ? call('routineInstances.done', { params: { id: v.id }, body: v.proof })
        : call('routineInstances.undo', { params: { id: v.id } }),
  );
}

export function useCreateTemplate() {
  return useStep7Write((body: CreateTemplateBody) => call('checklistTemplates.create', { body, idempotencyKey: newIdempotencyKey() }));
}

/** PATCH /checklist-templates/{id} (If-Match): rename, change segment, switch off. Never rewrites checklists made from it. */
export function useUpdateTemplate() {
  return useStep7Write((v: { id: string; version: number; body: UpdateTemplateBody }) =>
    call('checklistTemplates.update', { params: { id: v.id }, body: v.body, ifMatch: v.version }),
  );
}

/** POST /checklist-templates/{id}/items — append lines. */
export function useAddTemplateItems() {
  return useStep7Write((v: { id: string; body: AddTemplateItemsBody }) =>
    call('checklistTemplates.addItems', { params: { id: v.id }, body: v.body, idempotencyKey: newIdempotencyKey() }),
  );
}

/** PATCH /checklist-templates/{id}/items/{iid} (If-Match = template version): fix a line or switch it off. */
export function useUpdateTemplateItem() {
  return useStep7Write((v: { id: string; itemId: string; version: number; body: UpdateTemplateItemBody }) =>
    call('checklistTemplates.updateItem', { params: { id: v.id, iid: v.itemId }, body: v.body, ifMatch: v.version }),
  );
}

/** PATCH /client-checklists/{id} (If-Match = checklist version): rename or switch off (stops new runs). */
export function useUpdateClientChecklist() {
  return useStep7Write((v: { id: string; version: number; body: UpdateClientChecklistBody }) =>
    call('checklists.update', { params: { id: v.id }, body: v.body, ifMatch: v.version }),
  );
}

/** POST /client-checklists/{id}/items. */
export function useAddChecklistItems() {
  return useStep7Write((v: { id: string; body: AddChecklistItemsBody }) =>
    call('checklists.addItems', { params: { id: v.id }, body: v.body, idempotencyKey: newIdempotencyKey() }),
  );
}

/** PATCH /client-checklists/{id}/items/{iid} (If-Match = checklist version). A new due day applies from the next run. */
export function useUpdateChecklistItem() {
  return useStep7Write((v: { id: string; itemId: string; version: number; body: UpdateChecklistItemBody }) =>
    call('checklists.updateItem', { params: { id: v.id, iid: v.itemId }, body: v.body, ifMatch: v.version }),
  );
}

export function useApplyTemplate() {
  return useStep7Write((body: ApplyTemplateBody) => call('checklists.applyTemplate', { body, idempotencyKey: newIdempotencyKey() }));
}

export function useCreateClientChecklist() {
  return useStep7Write((v: { clientId: string; body: CreateClientChecklistBody }) =>
    call('checklists.create', { params: { id: v.clientId }, body: v.body, idempotencyKey: newIdempotencyKey() }),
  );
}

export function useCreateRoutine() {
  return useStep7Write((v: { clientId: string; body: CreateRoutineBody; key: string }) =>
    call('routines.create', { params: { id: v.clientId }, body: v.body, idempotencyKey: v.key }),
  );
}

export function useUpdateRoutine() {
  return useStep7Write((v: { clientId: string; id: string; version: number; body: UpdateRoutineBody }) =>
    call('routines.update', { params: { id: v.clientId, rid: v.id }, body: v.body, ifMatch: v.version }),
  );
}

export function useRemoveRoutine() {
  return useStep7Write((v: { clientId: string; id: string }) => call('routines.remove', { params: { id: v.clientId, rid: v.id } }));
}

export function useBulkRoutines() {
  return useStep7Write((v: { body: BulkRoutinesBody; key: string }) => call('routines.bulk', { body: v.body, idempotencyKey: v.key }));
}

// ---------------------------------------------------------------- wording

/** Prototype ckStatus chip colours: Done green, Missed red, Due today amber, upcoming plain, not started info. */
export const STATE_CHIP: Record<ChecklistItemState, string> = { done: 'hot', missed: 'cold', due_today: 'warm', upcoming: '', not_started: 'info' };

/** `2026-10` → "October". */
export function periodMonth(period: string): string {
  const [y, m] = period.split('-').map(Number);
  return new Date(Date.UTC(y!, (m ?? 1) - 1, 1)).toLocaleDateString('en-GB', { month: 'long', timeZone: 'UTC' });
}
